import { useMemo, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { useUnitStore } from '../store/unitStore';
import { useRealtimeStore } from '../store/realtimeStore';
import clsx from 'clsx';

export default function SettingsPage() {
  const { user, logout } = useAuthStore();
  const { units, selectedUnitId, selectedUnitIds, fetchUnits, setSelectedUnitId, setSelectedUnitIds } =
    useUnitStore();
  const { isConnected } = useRealtimeStore();

  const [busy, setBusy] = useState<string | null>(null);

  const selectedUnit = useMemo(() => {
    if (!selectedUnitId) return null;
    return units.find((b) => b.id === selectedUnitId) || null;
  }, [units, selectedUnitId]);

  const apiBaseUrl = (import.meta as any)?.env?.VITE_API_URL || '/api/v1';

  const resetView = async () => {
    setBusy('reset');
    try {
      setSelectedUnitIds([]);
      setSelectedUnitId(null);
      // Persisted via zustand persist; still safe to hard-clear the persisted selection key
      localStorage.removeItem('opmonitor-unit');
      window.location.reload();
    } finally {
      setBusy(null);
    }
  };

  const clearAllLocalCache = async () => {
    const ok = window.confirm('This will clear local UI caches (including login) and reload. Continue?');
    if (!ok) return;
    setBusy('clear');
    try {
      localStorage.removeItem('opmonitor-unit');
      localStorage.removeItem('opmonitor-auth');
      window.location.reload();
    } finally {
      setBusy(null);
    }
  };

  const doLogout = async () => {
    setBusy('logout');
    try {
      await logout();
      window.location.href = '/login';
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-command-accent tracking-wider">Settings</h1>
          <p className="text-sm text-gray-400">System + UI preferences for your current session.</p>
        </div>
        <div className={clsx(
          'px-3 py-1 rounded-lg border text-xs font-tactical',
          isConnected ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'
        )}>
          {isConnected ? 'LIVE' : 'OFFLINE'}
        </div>
      </div>

      {/* Account */}
      <div className="bg-command-panel border border-command-border rounded-xl p-5">
        <h2 className="text-sm font-bold text-gray-200 mb-4 uppercase tracking-wide">Account</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-command-bg border border-command-border">
            <div className="text-xs text-gray-500 font-tactical mb-1">User</div>
            <div className="text-gray-200 font-semibold">{user ? `${user.firstName} ${user.lastName}` : '—'}</div>
            <div className="text-xs text-gray-400 mt-1">{user?.email || ''}</div>
          </div>
          <div className="p-4 rounded-lg bg-command-bg border border-command-border">
            <div className="text-xs text-gray-500 font-tactical mb-1">Role / Unit</div>
            <div className="text-gray-200 font-semibold">{user?.role || '—'}</div>
            <div className="text-xs text-gray-400 mt-1">
              Unit ID: {user?.unitId || 'HQ'}
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={doLogout}
            disabled={busy !== null}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-semibold border transition-colors',
              busy ? 'opacity-60 cursor-not-allowed' : 'hover:bg-command-border',
              'bg-command-bg border-command-border text-gray-200'
            )}
          >
            Logout
          </button>
          <button
            onClick={clearAllLocalCache}
            disabled={busy !== null}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-semibold border transition-colors',
              busy ? 'opacity-60 cursor-not-allowed' : 'hover:bg-red-500/10',
              'bg-command-bg border-red-500/40 text-red-300'
            )}
          >
            Clear local cache
          </button>
        </div>
      </div>

      {/* View / Filters */}
      <div className="bg-command-panel border border-command-border rounded-xl p-5">
        <h2 className="text-sm font-bold text-gray-200 mb-4 uppercase tracking-wide">View</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-command-bg border border-command-border">
            <div className="text-xs text-gray-500 font-tactical mb-1">Selected Unit</div>
            <div className="text-gray-200 font-semibold">
              {selectedUnit ? `${selectedUnit.name} (${selectedUnit.code})` : 'All / HQ'}
            </div>
            <div className="text-xs text-gray-400 mt-1">selectedUnitId: {selectedUnitId ?? 'null'}</div>
          </div>
          <div className="p-4 rounded-lg bg-command-bg border border-command-border">
            <div className="text-xs text-gray-500 font-tactical mb-1">Multi-select Filters</div>
            <div className="text-gray-200 font-semibold">{selectedUnitIds.length}</div>
            <div className="text-xs text-gray-400 mt-1">{selectedUnitIds.join(', ') || '—'}</div>
          </div>
          <div className="p-4 rounded-lg bg-command-bg border border-command-border">
            <div className="text-xs text-gray-500 font-tactical mb-1">API Base URL</div>
            <div className="text-gray-200 font-semibold">{apiBaseUrl}</div>
            <div className="text-xs text-gray-400 mt-1">Used for all dashboard API calls</div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={async () => {
              setBusy('refresh');
              try {
                await fetchUnits();
              } finally {
                setBusy(null);
              }
            }}
            disabled={busy !== null}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-semibold border transition-colors',
              busy ? 'opacity-60 cursor-not-allowed' : 'hover:bg-command-border',
              'bg-command-bg border-command-border text-gray-200'
            )}
          >
            Refresh units
          </button>
          <button
            onClick={resetView}
            disabled={busy !== null}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-semibold border transition-colors',
              busy ? 'opacity-60 cursor-not-allowed' : 'hover:bg-command-border',
              'bg-command-bg border-command-border text-gray-200'
            )}
          >
            Reset view to HQ (All)
          </button>
        </div>
      </div>
    </div>
  );
}

